import React, { useState } from 'react'
import { SpiSdk } from '@local/spi-js'
import { useSpiInternal } from './SpiElementsProvider'

export type SpiPaymentElementProps = {
  errorFallback?: React.ReactNode
  /** Callback for when an initialization succeeds. Suggested to be used to message pass the initial selected value to the consumer.
   * Callback execution occurs in the SDK scope, and errors thrown here will not be caught in the consuming React application. */
  onInitSuccessCallback: Parameters<SpiSdk['initialize']>[0]
  /** Callback for when an initialization error occurs. Callback execution occurs in the SDK scope,
   * and errors thrown here will not be caught in the consuming React application. */
  onInitErrorCallback?: Parameters<SpiSdk['initialize']>[1]
  /** Callback for when the SPI input value changes */
  onChange: Parameters<SpiSdk['monitor']>[0]
  spiInitializationParams: {
    merchantId: string
    acceptAmex: boolean
    sessionSecret: string
    oauthToken: string
  }
  /** When an initialization errors occurs, throws error to the nearest error boundary */
  throwOnError?: boolean
  /** Custom injection point to allow us to show a skeleton */
  pendingElement?: React.ReactNode
}

export const SpiPaymentElement = ({
  throwOnError = false,
  errorFallback = null,
  ...props
}: SpiPaymentElementProps) => {
  const shouldThrowOnError = throwOnError
  const [mountingDomElementId] = useState(
    () => `spi-mount-anchor-${Math.random()}` // NOSONAR - safe for unique DOM id generation
  )
  const { spiSdk, status: spiSdkStatus } = useSpiInternal()
  const mountRef = React.useRef<HTMLDivElement | null>(null)
  const [initError, setInitError] = React.useState<Record<
    string,
    unknown
  > | null>(null)
  const stableOnInitSuccessCallback = React.useRef(props.onInitSuccessCallback)
  const stableOnInitErrorCallback = React.useRef(
    (
      ...args: Parameters<Exclude<typeof props.onInitErrorCallback, undefined>>
    ) => {
      props.onInitErrorCallback?.(...args)
      setInitError(...args)
    }
  )
  const spiChangeHandler = React.useRef(props.onChange)
  const [isMounted, setIsMounted] = React.useState(false)
  React.useEffect(() => {
    // A verbose and naive strategy to guard against callbacks causing the effect to re-run
    stableOnInitSuccessCallback.current = props.onInitSuccessCallback
    if (props.onInitErrorCallback) {
      stableOnInitErrorCallback.current = props.onInitErrorCallback
    }
    spiChangeHandler.current = props.onChange
  }, [props])

  React.useEffect(() => {
    let isMounted = true
    let iframe: HTMLIFrameElement | undefined

    const handleError = (
      ...args: Parameters<typeof stableOnInitErrorCallback.current>
    ) => {
      stableOnInitErrorCallback.current(...args)
      setInitError(...args)
    }

    if (spiSdkStatus === 'ready' && mountRef.current) {
      setInitError(null)
      const iframeOrIframePromise = spiSdk.initialize(
        (initializedEvent) => {
          setIsMounted(true)
          stableOnInitSuccessCallback.current(initializedEvent)
          // set up monitor on input changes
          spiSdk.monitor(spiChangeHandler.current)
        },
        handleError,
        {
          merchantId: props.spiInitializationParams.merchantId,
          domElementId: mountingDomElementId,
          sessionSecret: props.spiInitializationParams.sessionSecret,
          oauthToken: props.spiInitializationParams.oauthToken,
          acceptAmex: props.spiInitializationParams.acceptAmex
        },
        {
          // BLACK
          iconColor: '#252525',
          color: '#252525',
          // Input font family
          fontFamily:
            'Source-Sans-Pro, system-ui, -apple-system, "Helvetica Neue", Arial'
        }
      )
      if ('then' in iframeOrIframePromise) {
        iframeOrIframePromise
          .then((spiIframe) => {
            if (isMounted) {
              iframe = spiIframe
            }
          })
          // handle both promise-based and callback-based errors,
          // but will the errors duplicate? The API here is ambiguous
          .catch(handleError)
      } else if (isMounted) {
        iframe = iframeOrIframePromise
      }
    }
    return () => {
      isMounted = false
      if (iframe) {
        try {
          document.removeChild(iframe)
        } catch (e) {
          // swallow error when iframe is not in the DOM
          // -- this is likely not necessary, as the DOM should be cleaned by this point anyway
        }
      }
    }
  }, [
    props.spiInitializationParams.acceptAmex,
    props.spiInitializationParams.sessionSecret,
    props.spiInitializationParams.oauthToken,
    props.spiInitializationParams.merchantId,
    spiSdk,
    spiSdkStatus,
    mountingDomElementId
  ])

  if (initError || spiSdkStatus === 'error') {
    if (shouldThrowOnError) {
      throw new Error('Failed to mount.')
    }
    return <>{errorFallback}</>
  }

  if (props.pendingElement && (spiSdkStatus === 'loading' || !isMounted)) {
    return (
      <div ref={mountRef} id={mountingDomElementId}>
        {props.pendingElement}
      </div>
    )
  }

  return <div ref={mountRef} id={mountingDomElementId} />
}
